import {
  TAX_NUMBER_STATUS,
  TAX_NUMBER_TYPES,
} from "@/services/ecommerce/constants";
import COUNTRY_PRICE_OPTIONS from "../data/country-options-piecewise-price-model.json";
import { POSTAL_SHIPPING_ENABLED, SHIPPING_METHODS } from "./constants";

const TAX_SCHEME_BY_LOCATION = {
  AT: { scheme: TAX_NUMBER_TYPES.IOSS.value },
  BE: { scheme: TAX_NUMBER_TYPES.IOSS.value },
  BG: { scheme: TAX_NUMBER_TYPES.IOSS.value },
  HR: { scheme: TAX_NUMBER_TYPES.IOSS.value },
  CY: { scheme: TAX_NUMBER_TYPES.IOSS.value },
  CZ: { scheme: TAX_NUMBER_TYPES.IOSS.value },
  DK: { scheme: TAX_NUMBER_TYPES.IOSS.value },
  EE: { scheme: TAX_NUMBER_TYPES.IOSS.value },
  FI: { scheme: TAX_NUMBER_TYPES.IOSS.value },
  FR: { scheme: TAX_NUMBER_TYPES.IOSS.value },
  DE: { scheme: TAX_NUMBER_TYPES.IOSS.value },
  GR: { scheme: TAX_NUMBER_TYPES.IOSS.value },
  HU: { scheme: TAX_NUMBER_TYPES.IOSS.value },
  IE: { scheme: TAX_NUMBER_TYPES.IOSS.value },
  IT: { scheme: TAX_NUMBER_TYPES.IOSS.value },
  LV: { scheme: TAX_NUMBER_TYPES.IOSS.value },
  LT: { scheme: TAX_NUMBER_TYPES.IOSS.value },
  LU: { scheme: TAX_NUMBER_TYPES.IOSS.value },
  MT: { scheme: TAX_NUMBER_TYPES.IOSS.value },
  NL: { scheme: TAX_NUMBER_TYPES.IOSS.value },
  PL: { scheme: TAX_NUMBER_TYPES.IOSS.value },
  PT: { scheme: TAX_NUMBER_TYPES.IOSS.value },
  RO: { scheme: TAX_NUMBER_TYPES.IOSS.value },
  SK: { scheme: TAX_NUMBER_TYPES.IOSS.value },
  SI: { scheme: TAX_NUMBER_TYPES.IOSS.value },
  ES: { scheme: TAX_NUMBER_TYPES.IOSS.value },
  SE: { scheme: TAX_NUMBER_TYPES.IOSS.value },
  GB: {
    scheme: TAX_NUMBER_TYPES.UK_VAT.value, // Default for GB is UK VAT
    overrides: [
      // North Ireland uses IOSS
      {
        check: (pc) => !!pc && pc.toUpperCase().startsWith("BT"),
        scheme: TAX_NUMBER_TYPES.IOSS.value,
      },
    ],
  },
};

// In the past we were only using fedex so weight breaks were every 0.5kg.
// But the other couriers we use have different kinds of weight breaks
// (e.g. the lowest return helper chargeable weight is 100g).
// TODO: accept a courier param and rename the function getRoundedChargeableWeight
// so it's clear we are are rounding up to the nearest chargeable weight break
// and not doing a generic decimal place rounding.
export const getRoundedWeight = (weight) => {
  let roundedWeight = Math.ceil(parseFloat(weight) * 2) / 2;
  if (roundedWeight == 0) {
    roundedWeight = 0.5;
  }
  return roundedWeight;
};

export const isOversized = (parcel) => {
  const { length, width, height } = parcel;
  const dimensions = [
    parseFloat(length),
    parseFloat(width),
    parseFloat(height),
  ].sort((a, b) => a - b);
  return dimensions[2] + 2 * (dimensions[1] + dimensions[0]) > 330;
};

export const isTooHeavyForSinglePersonLift = (parcel) => {
  const { weight } = parcel;
  return parseFloat(weight) > 30;
};

export const getParcelChargeableWeight = (parcel) => {
  let volumetricWeight = 0;
  const actualWeight = parseFloat(parcel.weight) || 0;
  if (parcel.length && parcel.width && parcel.height) {
    volumetricWeight =
      (parseFloat(parcel.length) *
        parseFloat(parcel.width) *
        parseFloat(parcel.height)) /
      5000;
  }
  const valuesToCompare = [volumetricWeight, actualWeight];
  if (isOversized(parcel)) {
    valuesToCompare.push(68);
  }
  return Math.round(Math.max(...valuesToCompare) * 100) / 100;
};

export const isRecipientInICS2 = (recipientCountry) => {
  // ICS2 countries are the EU countries + Switzerland, Norway, Iceland, Liechtenstein
  const ics2Countries = [
    "AT",
    "BE",
    "BG",
    "HR",
    "CY",
    "CZ",
    "DK",
    "EE",
    "FI",
    "FR",
    "DE",
    "GR",
    "HU",
    "IE",
    "IT",
    "LV",
    "LT",
    "LU",
    "MT",
    "NL",
    "PL",
    "PT",
    "RO",
    "SK",
    "SI",
    "ES",
    "SE",
    "IS",
    "LI",
    "NO",
    "CH",
  ];
  return ics2Countries.includes(recipientCountry);
};

export const isInFedExEuropeZone = (countryCode) => {
  // Europe and Pseudo Europe
  return [
    "GB",
    "DE",
    "FR",
    "IT",
    "ES",
    "NL",
    "BE",
    "FI",
    "SK",
    "CZ",
    "SE",
    "DK",
    "MT",
    "SI",
    "CY",
    "RU",
    "PL",
    "RS",
    "CH",
    "RO",
    "EE",
    "NO",
    "PT",
    "FO",
    "AD",
    "IS",
    "IL",
    "IE",
    "HU",
    "HR",
    "LI",
    "GR",
    "AT",
    "UA",
    "LT",
    "LU",
    "TR",
    "GL",
    "BG",
    "MC",
    "LV",
  ].includes(countryCode);
};

/**
 * Checks if a given location participates in the IOSS system.
 * @param {string} countryCode - The two-letter ISO country code.
 * @param {string|null} postalCode - The postal code to check.
 * @returns {boolean} - True if the location participates in the IOSS system, otherwise false.
 */
export const isIossSchemeLocation = (countryCode, postalCode = null) => {
  return (
    getApplicableTaxNumberTypeForLocation(countryCode, postalCode) ===
    TAX_NUMBER_TYPES.IOSS.value
  );
};

export const isPostalToIossSchemeLocation = (
  shippingMethod,
  countryCode,
  postalCode = null
) => {
  return (
    POSTAL_SHIPPING_ENABLED &&
    shippingMethod === SHIPPING_METHODS.POSTAL.value &&
    isIossSchemeLocation(countryCode, postalCode)
  );
};

/**
 * Determines the applicable tax number type for a given location
 *
 * @param {string} countryCode - ISO 2-letter country code
 * @param {string} postalCode - Postal/ZIP code for location-specific checks
 * @returns {string|null} - Tax number type from TAX_NUMBER_TYPES enum, or null if no match
 */
export const getApplicableTaxNumberTypeForLocation = (
  countryCode,
  postalCode
) => {
  if (!countryCode) return null;

  const countryEntry = TAX_SCHEME_BY_LOCATION[countryCode.toUpperCase()];
  if (!countryEntry) return null;

  if (countryEntry.overrides?.length) {
    for (const rule of countryEntry.overrides) {
      // Assume rule.check exists and is a function
      if (rule.check(postalCode)) {
        return rule.scheme;
      }
    }
  }
  return countryEntry.scheme ?? null;
};

export const isShipmentEligibleForDDP = (
  recipientCountry,
  recipientPostalCode,
  shippingMethod,
  taxNumberByTypes
) => {
  if (shippingMethod === SHIPPING_METHODS.EXPRESS.value) return true;
  if (shippingMethod === SHIPPING_METHODS.STANDARD.value) return false;

  const taxScheme = getApplicableTaxNumberTypeForLocation(
    recipientCountry,
    recipientPostalCode
  );
  if (!taxScheme) return false;

  switch (taxScheme) {
    case TAX_NUMBER_TYPES.IOSS.value:
      return true;
    case TAX_NUMBER_TYPES.UK_VAT.value:
      return (
        taxNumberByTypes[taxScheme]?.status === TAX_NUMBER_STATUS.APPROVED.value
      );
    default:
      return false;
  }
};

export const getShippingQuote = (roundedWeight, countryCode) => {
  const shippingOption = COUNTRY_PRICE_OPTIONS[countryCode];
  if (shippingOption) {
    return Number(
      piecewisePriceFunc({ roundedWeight, ...shippingOption }).toFixed(2)
    );
  }
};

const piecewisePriceFunc = ({ roundedWeight, breakpoints, gradients, c }) => {
  let price = c;

  for (let i = 0; i < breakpoints.length; i++) {
    if (roundedWeight < breakpoints[i]) {
      // if weight is lower than the breakpoint, add the segment up to the weight and return
      price += gradients[i] * (roundedWeight - (breakpoints[i - 1] || 0));
      return price;
    } else {
      // else, add the entire segment
      price += gradients[i] * (breakpoints[i] - (breakpoints[i - 1] || 0));
    }
  }
  // If weight is higher than all breakpoints, add the last segment
  price +=
    gradients[breakpoints.length] *
    (roundedWeight - breakpoints[breakpoints.length - 1]);
  return price;
};
